import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    inject,
} from '@angular/core';
import { NgClass, UpperCasePipe } from '@angular/common';
import { confirm } from 'devextreme/ui/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import moment from 'moment';
import FileSaver from 'file-saver';
import {
    debounce as _debounce,
    DebouncedFunc,
    each as _each,
    find as _find,
    get as _get,
    toNumber as _toNumber,
    trim as _trim,
} from 'lodash';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { DxBoxComponent } from 'devextreme-angular/ui/box';
import {
    DxiItemComponent,
    DxoPagingComponent,
    DxoEditingComponent,
    DxoRowDraggingComponent,
    DxiColumnComponent,
} from 'devextreme-angular/ui/nested';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxDateBoxComponent } from 'devextreme-angular/ui/date-box';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxTextAreaComponent } from 'devextreme-angular/ui/text-area';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import {
    MessageType,
    NotificationService,
    PrintSheetService,
    RenderPersistReportService,
    RenderedFileFormat,
    UtilService,
} from 'src/app/shared/services';
import {
    STORES,
    StoreConfig,
    sellerFor,
    storeById,
} from 'src/app/shared/config/store-config';

import { branchList } from 'src/app/shared/config/branch-list';
import { FormatNumberPipe } from 'src/app/shared/pipes';
import {
    Counterparty,
    CounterpartyFinderComponent,
} from 'src/app/shared/components/counterparty-finder/counterparty-finder.component';
import { environment } from 'src/environments/environment';
import {
    LoadedOrderInfo,
    ReceiptNoteInput,
    getInitialReceiptNoteInput,
    inputMomentFormat,
} from './models/receipt-note-input';
import {
    buildReceiptNoteParam,
    itemTotal,
    netPrice,
} from './models/build-receipt-note-param';
import {
    BillingCustomer,
    FetchedOrder,
    FoundOrder,
    PlatformOrderService,
} from './services/platform-order.service';
import { candidateStores } from './models/order-no-pattern';

// Issues a ใบเสร็จรับเงิน for one marketplace order: the operator types the
// order number, the order is pulled from the platform API (and the customer's
// own billing request, when they filed one), the form is corrected as needed,
// and the `receipt-note` template renders it — to a print preview, a
// download, or a Drive link to paste into the chat.
//
// Naming the shop is optional: the order number's shape says which platform
// it belongs to, so every shop on that platform is asked at once and the one
// holding the order answers. Picking a shop narrows that search to it, but
// only a shop the OPERATOR picked and only for a number that shop's platform
// could own — the picker also shows whatever the last load found, and that
// must not decide where the next order number is looked for.
//
// Lazada's chat accepts neither PDFs nor outside links, only images, so a
// Lazada receipt is a JPEG wherever a file is produced; the other platforms
// get a PDF.
@Component({
    selector: 'app-receipt-note',
    templateUrl: './receipt-note.component.html',
    styleUrls: ['./receipt-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxLoadPanelComponent,
        DxBoxComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxTextBoxComponent,
        DxNumberBoxComponent,
        DxDateBoxComponent,
        DxSwitchComponent,
        DxSelectBoxComponent,
        DxTextAreaComponent,
        DxDataGridComponent,
        DxPopupComponent,
        DxoPagingComponent,
        DxoEditingComponent,
        DxoRowDraggingComponent,
        DxiColumnComponent,
        NgClass,
        UpperCasePipe,
        FormatNumberPipe,
        CounterpartyFinderComponent,
    ],
})
export class ReceiptNoteComponent implements OnDestroy {
    private cdref = inject(ChangeDetectorRef);
    private printSheetService = inject(PrintSheetService);
    private renderPersistService = inject(RenderPersistReportService);
    private platformOrderService = inject(PlatformOrderService);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);

    stores: StoreConfig[] = STORES;
    branchList = branchList;

    inputValues: ReceiptNoteInput = getInitialReceiptNoteInput();
    loadedOrder: LoadedOrderInfo | null = null;
    shareData: {
        fileName: string;
        sharedUrl: string;
    } | null = null;
    isLoading = false;
    isExporting = false;
    finderPopupVisible = false;
    // Whether the shop in the picker is the operator's choice or just what
    // the last load filled in. Only a choice narrows the next search: a shop
    // left behind by the previous order would otherwise send the next order
    // number to one shop of its platform and report it missing.
    private storePickedByHand = false;
    onInputsUpdated: DebouncedFunc<() => void>;

    constructor() {
        this.onInputsUpdated = _debounce(this.internalOnInputsUpdated, 200);
        this.onReorder = this.onReorder.bind(this);
    }

    ngOnDestroy(): void {
        // A trailing debounced call would run detectChanges() on the destroyed
        // view — navigating away mid-edit is enough to trigger it.
        this.onInputsUpdated.cancel();
    }

    get busy(): boolean {
        return this.isLoading || this.isExporting;
    }

    get total(): number {
        return itemTotal(this.inputValues.items);
    }

    get net(): number {
        return netPrice(this.inputValues);
    }

    // 'jpg' for Lazada, 'pdf' otherwise — the one rule behind every file
    // this page produces. Follows the SELECTED store so the buttons read
    // right before an order is loaded.
    get fileFormat(): RenderedFileFormat {
        const storeId = this.inputValues.source.storeId;
        return storeId && storeById(storeId).platform === 'lazada'
            ? 'jpg'
            : 'pdf';
    }

    get fileFormatLabel(): string {
        return this.fileFormat.toUpperCase();
    }

    loadOrder(): void {
        if (this.busy) {
            return;
        }
        const orderNo = _trim(this.inputValues.source.orderNo);
        if (!orderNo) {
            this.notifyError(new Error('กรุณากรอกเลขที่คำสั่งซื้อ'));
            return;
        }
        this.isLoading = true;
        // A stale link from the previous order must not sit next to the new
        // one's form.
        this.shareData = null;
        this.platformOrderService
            .findOrder(orderNo, this.storesToSearch(orderNo))
            .pipe(
                // The billing request is keyed on the shop, so it can only be
                // asked for once the search has said which shop that is.
                switchMap((found: FoundOrder) =>
                    forkJoin([
                        of(found),
                        // Advisory: a failed lookup still loads the order from
                        // platform data, it just says so.
                        this.platformOrderService
                            .fetchBillingCustomer(found.store, orderNo)
                            .pipe(
                                catchError((error) => {
                                    this.notifyError(
                                        error,
                                        'Billing request lookup failed — customer from platform data',
                                    );
                                    return of(null);
                                }),
                            ),
                    ]),
                ),
            )
            .subscribe(
                ([found, billingCustomer]: [
                    FoundOrder,
                    BillingCustomer | null,
                ]) => {
                    this.applyFetchedOrder(
                        found.store,
                        found.order,
                        billingCustomer,
                    );
                    this.isLoading = false;
                    this.cdref.detectChanges();
                    this.notificationService.pushMessage({
                        message: `Order ${found.order.orderNo} loaded from ${
                            found.store.title
                        }${
                            billingCustomer
                                ? ' with the customer’s billing request'
                                : ''
                        }.`,
                        messageType: MessageType.success,
                    });
                },
                (error) => {
                    this.isLoading = false;
                    this.cdref.detectChanges();
                    this.notifyError(error);
                },
            );
    }

    // The store select's onValueChanged, which also fires when a finished load
    // writes the shop it found into the form. DevExtreme sets `event` only for
    // a change the operator made, which is exactly the distinction the search
    // needs.
    onStorePicked(e: any): void {
        if (e.event) {
            this.storePickedByHand = !!e.value;
        }
        this.onInputsUpdated();
    }

    clearForm(): void {
        confirm('Filled data will be reset.', 'Confirm Clear Data ?').then(
            (result: boolean) => {
                if (result) {
                    const storeId = this.inputValues.source.storeId;
                    this.inputValues = getInitialReceiptNoteInput();
                    // The store is a standing choice, not part of the order.
                    this.inputValues.source.storeId = storeId;
                    this.loadedOrder = null;
                    this.shareData = null;
                    this.cdref.detectChanges();
                    this.notificationService.pushMessage({
                        message: 'Form is cleared.',
                        messageType: MessageType.info,
                    });
                }
            },
        );
    }

    previewPdf(): void {
        const built = this.buildOrNotify();
        if (!built || this.busy) {
            return;
        }
        this.isExporting = true;
        this.printSheetService
            .printReceiptNote([built.param], `ใบเสร็จรับเงิน ${built.orderNo}`)
            .subscribe(
                () => {
                    this.notificationService.pushMessage({
                        message: 'Receipt preview is open.',
                        messageType: MessageType.success,
                    });
                },
                (error) => {
                    this.notifyError(error);
                    // RxJS never calls complete after an error, and the
                    // pop-up-blocked preview tells the user to print again —
                    // which the load panel would otherwise make impossible.
                    this.isExporting = false;
                    this.cdref.detectChanges();
                },
                () => {
                    this.isExporting = false;
                    this.cdref.detectChanges();
                },
            );
    }

    downloadFile(): void {
        const built = this.buildOrNotify();
        if (!built || this.busy) {
            return;
        }
        const format = this.fileFormat;
        const fileName = this.fileNameFor(built.orderNo, format);
        this.isExporting = true;
        this.printSheetService
            .renderReceiptNoteBlob([built.param], format)
            .subscribe(
                (blob: Blob) => {
                    FileSaver.saveAs(blob, fileName);
                    this.notificationService.pushMessage({
                        message: `${fileName} is downloaded.`,
                        messageType: MessageType.success,
                    });
                    this.isExporting = false;
                    this.cdref.detectChanges();
                },
                (error) => {
                    this.notifyError(error);
                    this.isExporting = false;
                    this.cdref.detectChanges();
                },
            );
    }

    saveAndShare(): void {
        // Before the guard: a rejected save must not leave the previous
        // document's share URL on screen next to the new form.
        this.shareData = null;
        const built = this.buildOrNotify();
        if (!built || this.busy) {
            return;
        }
        const format = this.fileFormat;
        const fileName = this.fileNameFor(built.orderNo, format);
        this.isExporting = true;
        this.renderPersistService
            .renderAndGetSharedUrl({
                renderRequest:
                    this.printSheetService.buildReceiptNoteRenderRequest(
                        [built.param],
                        format,
                    ),
                reportStoreRequest: {
                    fileName,
                    parents: [environment.receiptNoteDriveFolderId],
                },
            })
            .subscribe(
                (response) => {
                    this.shareData = {
                        fileName: response.name,
                        sharedUrl: response.webViewLink,
                    };
                    this.notificationService.pushMessage({
                        message: `${response.name} shared URL is now available.`,
                        messageType: MessageType.success,
                    });
                    this.isExporting = false;
                    this.cdref.detectChanges();
                },
                (error) => {
                    this.notifyError(error);
                    this.isExporting = false;
                    this.cdref.detectChanges();
                },
            );
    }

    openSharedUrl(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            window.open(sharedUrl, '_blank');
        }
    }

    copySharedUrlToClipboard(): void {
        const sharedUrl = _get(this.shareData, 'sharedUrl');
        if (sharedUrl) {
            navigator.clipboard.writeText(sharedUrl).then(
                () => {
                    this.notificationService.pushMessage({
                        message: 'URL copied to clipboard.',
                        messageType: MessageType.success,
                        displayTime: 100,
                    });
                },
                (e) => {
                    console.error(e);
                },
            );
        }
    }

    // The finder keeps its last search between openings, so reopening to pick
    // another branch of the same company does not cost a second lookup.
    openCounterpartyFinder(): void {
        this.finderPopupVisible = true;
        this.cdref.detectChanges();
    }

    // Fills the customer block from a DBD lookup: legal name, tax id, branch
    // and the registered address. Overwrites what is there — picking a
    // counterparty IS the operator saying "this is the customer" — but only
    // for fields the record actually carries; an empty one keeps the current
    // text. Everything stays editable afterwards. The pick also closes the
    // finder popup, putting the filled fields back in view.
    onCounterpartySelected(counterparty: Counterparty): void {
        const customer = this.inputValues.customerInfo;
        customer.name = _trim(counterparty.companyName) || customer.name;
        customer.taxId = _trim(counterparty.taxId) || customer.taxId;
        customer.branch =
            _trim(counterparty.branchDisplayText) || customer.branch;
        customer.address = _trim(counterparty.address) || customer.address;
        this.finderPopupVisible = false;
        this.cdref.detectChanges();
        this.notificationService.pushMessage({
            message: `Customer filled from DBD: ${customer.name}`,
            messageType: MessageType.success,
        });
    }

    onSaved(): void {
        this.generateItemOrderNumber();
        this.onInputsUpdated();
    }

    onReorder(e: any): void {
        const visibleRows = e.component.getVisibleRows();
        const toIndex = this.inputValues.items.indexOf(
            visibleRows[e.toIndex].data,
        );
        const fromIndex = this.inputValues.items.indexOf(e.itemData);
        this.inputValues.items.splice(fromIndex, 1);
        this.inputValues.items.splice(toIndex, 0, e.itemData);
        this.generateItemOrderNumber();
    }

    // Grid setCellValue for the unit price and quantity columns: the amount
    // column follows them, so an edited row never carries a stale amount into
    // the document. Arrow properties because DevExtreme calls them unbound.
    setUnitPrice = (newData: any, value: any, currentRowData: any): void => {
        newData.unitPrice = value;
        newData.amount = ReceiptNoteComponent.rowAmount(
            value,
            currentRowData.quantity,
        );
    };

    setQuantity = (newData: any, value: any, currentRowData: any): void => {
        newData.quantity = value;
        newData.amount = ReceiptNoteComponent.rowAmount(
            currentRowData.unitPrice,
            value,
        );
    };

    private static rowAmount(unitPrice: unknown, quantity: unknown): number {
        return (
            Math.round(_toNumber(unitPrice) * _toNumber(quantity) * 100) / 100
        );
    }

    private applyFetchedOrder(
        store: StoreConfig,
        order: FetchedOrder,
        billingCustomer: BillingCustomer | null,
    ): void {
        const input = getInitialReceiptNoteInput();
        input.source.storeId = store.storeId;
        // The picker now shows where the order was FOUND, not a choice the
        // operator made — the search may have written a different shop over
        // their pick, and either way this value must not narrow the next
        // lookup. A pick is spent the moment a load lands.
        this.storePickedByHand = false;
        input.source.orderNo = order.orderNo;
        const receiptMoment = order.paidAt || order.createdAt || moment();
        input.docInfo.date = receiptMoment.format(inputMomentFormat);
        input.docInfo.remark = '';
        const createdIsoDate = order.createdAt
            ? order.createdAt.format(inputMomentFormat)
            : '';
        if (billingCustomer) {
            input.customerInfo.name = billingCustomer.name;
            input.customerInfo.address = billingCustomer.address;
            input.customerInfo.taxId = billingCustomer.taxId;
            input.customerInfo.branch = billingCustomer.branch;
        } else {
            input.customerInfo.name = order.customer.name;
            input.customerInfo.address = order.customer.address;
        }
        input.amounts.discount = Math.round(order.discount * 100) / 100;
        input.amounts.shippingCost = Math.round(order.shippingCost * 100) / 100;
        input.items = order.items;
        this.inputValues = input;
        this.generateItemOrderNumber();
        this.loadedOrder = {
            storeId: store.storeId,
            platform: order.platform,
            orderNo: order.orderNo,
            createdIsoDate,
            customerSource: billingCustomer ? 'billing-request' : 'platform',
            paymentMethod: order.paymentMethod,
        };
    }

    private buildOrNotify(): {
        param: ReturnType<typeof buildReceiptNoteParam>;
        orderNo: string;
    } | null {
        this.internalOnInputsUpdated();
        const store = this.storeOrNotify();
        if (!store) {
            return null;
        }
        const orderNo = _trim(this.inputValues.source.orderNo);
        if (!orderNo) {
            this.notifyError(new Error('กรุณากรอกเลขที่คำสั่งซื้อ'));
            return null;
        }
        if (this.inputValues.items.length === 0) {
            this.notifyError(
                new Error(
                    'ไม่มีรายการสินค้า — โหลดคำสั่งซื้อหรือเพิ่มรายการก่อน',
                ),
            );
            return null;
        }
        const seller = sellerFor({ storeId: store.storeId });
        return {
            param: buildReceiptNoteParam(this.inputValues, store, seller),
            orderNo,
        };
    }

    // The shops to ask for this order number: every shop the number's shape
    // allows, narrowed to one only when the operator picked that one AND it
    // is among them. Both halves matter — the picker also holds whatever the
    // last load put there, and even a real choice cannot own an order number
    // from another platform.
    private storesToSearch(orderNo: string): StoreConfig[] {
        const candidates = candidateStores(orderNo, this.stores);
        if (!this.storePickedByHand) {
            return candidates;
        }
        const storeId = this.inputValues.source.storeId;
        const picked = _find(
            candidates,
            (store: StoreConfig) => store.storeId === storeId,
        );
        return picked ? [picked] : candidates;
    }

    private storeOrNotify(): StoreConfig | null {
        const storeId = this.inputValues.source.storeId;
        if (!storeId) {
            this.notifyError(new Error('กรุณาเลือกร้านค้าก่อน'));
            return null;
        }
        return storeById(storeId);
    }

    private fileNameFor(orderNo: string, format: RenderedFileFormat): string {
        return `receipt-${orderNo.replace(/[^A-Za-z0-9_-]+/g, '_')}.${format}`;
    }

    private internalOnInputsUpdated(): void {
        this.cdref.detectChanges();
    }

    private generateItemOrderNumber(): void {
        _each(this.inputValues.items, (item, index) => {
            item.itemNo = index + 1;
        });
    }

    private notifyError(error: unknown, label?: string): void {
        console.error(error);
        const errorMessage = this.utilService.errorToString(error);
        this.notificationService.pushMessage({
            displayTime: 6000,
            message: label ? `${label}: ${errorMessage}` : errorMessage,
            messageType: MessageType.error,
        });
    }
}
